import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import {
  Paper, TextInput, PasswordInput, Button, Text, Center, Stack, Alert,
  ActionIcon, useMantineColorScheme, useComputedColorScheme, ThemeIcon,
  Group, Anchor,
} from '@mantine/core'
import { useForm, Controller } from 'react-hook-form'
import { IconShieldOff, IconSun, IconMoon, IconLeaf } from '@tabler/icons-react'
import { useAuth } from '@/context/AuthContext'

interface LoginFormValues {
  email: string
  password: string
}

export default function Login() {
  const [error, setError] = useState('')
  const [forbidden, setForbidden] = useState(false)
  const { login } = useAuth()
  const { setColorScheme } = useMantineColorScheme()
  const computedColorScheme = useComputedColorScheme('light')
  const isDark = computedColorScheme === 'dark'
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    defaultValues: { email: '', password: '' },
  })

  useEffect(() => {
    if (searchParams.get('error') === 'forbidden') {
      setForbidden(true)
    }
  }, [searchParams])

  const onSubmit = handleSubmit(async (values) => {
    setError('')
    try {
      await login(values.email, values.password)
      navigate('/')
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Нэвтрэхэд алдаа гарлаа')
    }
  })

  return (
    <Center h="100vh" style={{ backgroundColor: isDark ? 'var(--mantine-color-dark-8)' : 'var(--mantine-color-gray-0)' }}>
      <div style={{ position: 'absolute', top: 16, right: 16 }}>
        <ActionIcon
          variant="subtle"
          onClick={() => setColorScheme(isDark ? 'light' : 'dark')}
          size="lg"
          radius="md"
          color={isDark ? 'yellow' : 'blue'}
        >
          {isDark ? <IconSun size={18} /> : <IconMoon size={18} />}
        </ActionIcon>
      </div>
      <Paper shadow="md" p="xl" w={400} radius="md">
        <Center mb="md">
          <Group gap="xs">
            <ThemeIcon size={44} radius="md" variant="gradient" gradient={{ from: 'green', to: 'teal', deg: 135 }}>
              <IconLeaf size={26} stroke={1.5} />
            </ThemeIcon>
          </Group>
        </Center>
        <Text fw={700} size="lg" ta="center" mb="xs">NatureMed</Text>
        <Text size="sm" c="dimmed" ta="center" mb="lg">Админ самбар руу нэвтрэх</Text>

        {forbidden && (
          <Alert icon={<IconShieldOff size={18} />} color="red" mb="md" onClose={() => setForbidden(false)} withCloseButton>
            Хандах эрх хүрэлцэхгүй байна. Дахин нэвтэрнэ үү.
          </Alert>
        )}

        <form onSubmit={onSubmit}>
          <Stack>
            <Controller
              name="email"
              control={control}
              rules={{
                required: 'Имэйл хаяг буруу байна',
                pattern: { value: /^\S+@\S+\.\S+$/, message: 'Имэйл хаяг буруу байна' },
              }}
              render={({ field }) => (
                <TextInput
                  label="Имэйл"
                  placeholder="admin@naturemed.mn"
                  {...field}
                  error={errors.email?.message}
                />
              )}
            />
            <Controller
              name="password"
              control={control}
              rules={{
                required: 'Нууц үг хамгийн багадаа 6 тэмдэгт байх ёстой',
                minLength: { value: 6, message: 'Нууц үг хамгийн багадаа 6 тэмдэгт байх ёстой' },
              }}
              render={({ field }) => (
                <PasswordInput
                  label="Нууц үг"
                  placeholder="Нууц үг"
                  {...field}
                  error={errors.password?.message}
                />
              )}
            />
            {error && <Text size="sm" c="red">{error}</Text>}
            <Button type="submit" fullWidth loading={isSubmitting} disabled={isSubmitting}>Нэвтрэх</Button>
            <Text size="sm" c="dimmed" ta="center">
              Бүртгэл байхгүй юу?{' '}
              <Anchor component={Link} to="/register" size="sm">Бүртгүүлэх</Anchor>
            </Text>
          </Stack>
        </form>
      </Paper>
    </Center>
  )
}
